import base from "@/fstore";
import validation from "@/fstore/validation";
import helper from "@/fstore/helpers";

const db = base.db;
const collection = "userdata";
const model = db.collection(collection);

const template = {
  uid: {
    type: "string",
    required: true
  },
  userNum: {
    type: "number",
    required: true
  },
  address: {
    type: "string",
    default: ""
  },
  dob: {
    type: "string",
    default: ""
  },
  fname: {
    type: "string",
    default: ""
  },
  lname: {
    type: "string",
    default: ""
  },
  gender: {
    type: "string",
    enums: ["Male", "Female", "Other"],
    default: ""
  },
  email: {
    type: "string",
    required: true
  },
  skills: {
    type: "object",
    default: []
  },
  positions: {
    type: "object",
    default: []
  },
  remote: {
    type: 'boolean',
    default: false
  },
  cities: {
    type: "object",
    default: []
  },
  resumes: {
    type: "object",
    default: []
  },
  role: {
    type: "string",
    required: true
  },
  profileDesc: {
    type: "string",
    default: ""
  },
  verified: {
    type: 'boolean',
    default: false
  },
  setup: {
    type: 'boolean',
    default: false
  },
  notifications: {
    type: "object",
    default: {
      initialExperience: false
    }
  },
  experience:{
    type: 'number',
    default: 0
  },
  willingToRelocate:{
    type: 'boolean',
    default: false
  }
};

const getTemplate = () => {
  return helper.getTemplate(template);
}

const currentUser = () => {
  return base.auth().currentUser;
};

const getAllUsers = async () => {
  // verify that current user has admin role
  var currentUserData = base.auth().currentUser;
  
  var usershot = await model.doc(currentUserData.uid).get()
  var user = usershot.data();

  if (user.role != "admin"){
    return [];
  }

  var userSnapshots = await model.get();

  let users  = userSnapshots.empty? [] : userSnapshots.docs.map(doc => {
    return doc.data();
  });

  return users;
}

const _getNextAvailableNumber = async () => {
   const doc = await db.collection("siteDocuments").doc('nextAvailable').get();

    const data = doc.data();

    const num = data.userNum;

    return num;
}

const create = async user => {
  try {
    // create the user account
    const createdUser = await base
      .auth()
      .createUserWithEmailAndPassword(user.email, user.password);

    if (createdUser === "auth/email-already-in-use") {
      return {
        errors: ["An account already exists with this email."]
      };
    }

    // do not log the user in just yet
    await base.auth().signOut();

    delete user.password;
    user.created = new Date();
    user.uid = createdUser.user.uid;
    user.userNum = await _getNextAvailableNumber()

    const response = await validation.validateModel(db, user, template);
    if (response.errors.length) {
      return response;
    }

    await model.doc(createdUser.user.uid).set(response.doc);

    // set the new available num
    let nextAvailableUserNum = ++user.userNum;

    db.collection('siteDocuments').doc('nextAvailable').set({
      userNum: nextAvailableUserNum
    }, {merge:true});

    return response;
  } catch (err) {
    console.error("ERROR!!! ", err);
    return err;
  }
}


const getById = async id => {
  let user = await model.doc(id).get();

  if (user.exists) {
    user = user.data();
    if (!user.uid) user.uid = id;

    // update the doc with any missing values from the template.
    user = helper.updateModel(user, template)
    return user;
  }

  return new Error({ code: 404, message: `User ${id} was not found` });
}

const getByEmail = async email => {
  let user = await model.where("email", "==", email).get();

  if(user.exists){
    user = user.data();
    if(!user.uid) user.uid = id;
    return user
  }

  return new Error({code: 404, message: `User ${email} was not found`});
}

const deleteById = async id => {
  try {
    const response = await model.doc(id).delete();

    // attempt to remove the actual user

    return response;
  } catch (error) {
    return new Error(`Failed to delete user '${id}'`);
  }
};

const verify = async userId => {
  return await model.doc(userId).set({ verified: true }, { merge: true });
};

const searchByLike = async term => {
  const refs = await model.get();

  const candidates = []

  if (!refs.docs.length)
    return candidates

  refs.docs.forEach(doc => {
    candidate = doc.data();

    let matched = candidate.positions.filter(position => position.includes(term));

    if (matched > 0)
      candidates.push(candidate)
  });

  return candidates;

}

const getCandidatesForJob = async job => {
  let snapshot = await model.where('cities', 'array-contains', job.location).get()

  if(snapshot.empty && !job.isRemote) {
    return {
      candidates: [],
      numCandidates: 0,
      numScreenings: 0,
      numResumes: 0
    };
  }

  let remotedocs = [];
  const jobRef = await db.doc(`job/${job.uid}`);

  if(job.isRemote) {
    const ids = snapshot.docs.map(doc => doc.id)
    let remotessnap = await model.where('remote', '==', true).get()
    
    // filter out remote candidates who were captured from the location already
    remotedocs = remotessnap.docs.filter(candidate => ids.indexOf(candidate.id) === -1);
  }

  let candidates = snapshot.empty? [] : snapshot.docs.map(doc => {
    const data = doc.data();
    const location = job.location.city;

    let skillsMatched = 0;

    data.skills.forEach(skill => {
      job.skills.forEach(jobSkill => {
        if(jobSkill.name == skill.name) ++skillsMatched;
      });
    });

    if(skillsMatched === 0)
      return null;

    return {
      uid: data.uid,
      profileDesc: data.profileDesc,
      skillsMatched,
      certifications: 1,
      location,
      skills: data.skills,
      positions: data.positions,
      workRemote: data.workRemote,
      userNum: data.userNum
    }
  });

  if(remotedocs){
    let remoteCandidates = remotedocs.map(doc => {
      const data = doc.data()
  
      // will map to either the city name if the user's selected sity is here, or remote if not
      // note that remote will only show up if remote is available on the search
      const location = 'remote';
  
      let skillsMatched = 0
  
      data.skills.forEach(skill => {
        job.skills.forEach(jobSkill => {
          if(jobSkill.name == skill.name) ++skillsMatched;
        });
      });
  
      if(skillsMatched === 0)
        return null;
  
      
      return {
        uid: data.uid,
        profileDesc: data.profileDesc,
        skillsMatched,
        location,
        skills: data.skills,
        positions: data.positions,
        workRemote: data.workRemote,
        userNum: data.userNum
      }
    });

    candidates.push(...remoteCandidates);
  }

  // filtering out nulls here removes candidates whose skillsmatched was 0.
  candidates = candidates.filter(candidate => candidate !== null)

  // go through and filter out all candidates who have a screening
  candidates = candidates.filter(async (candidate) => {
    const userRef = await db.doc(`userdata/${candidate.uid}`);
    const screening = await db.collection("screening").where("jobRef", "==", jobRef).where("dateDeleted", "==", null).where("userRef", "==", userRef).get();

    return !screening.empty;
  });

  // filter out candidates who the company has already removed from this job.
  candidates = candidates.filter((candidate) => job.removedCandidates.indexOf(candidate.uid) === -1);

  const sortedCandidates = candidates.sort((a, b) => {
    if(a.skillsMatched < b.skillsMatched) return 1;
    if(a.skillsMatched > b.skillsMatched) return -1;

    return 0;
  });

  let screenings = await db.collection("screening").where("jobRef", "==", jobRef).where("dateDeleted", "==", null).where("status.rejected", "==", false).where("connectionRequest.accepted", "==", false).get();
  if(screenings.length > 0) {
    screenings = screenings?.filter((screening) => job.removedCandidates.indexOf(screening.userRef.id) === -1)
  }

  let resumes = await db.collection ("screening").where("jobRef", "==", jobRef).where("dateDeleted", "==", null).where("status.rejected", "==", false).where("connectionRequest.accepted", "!=", false).get();
  if (resumes.length > 0) {
    resumes = resumes.filter((resume) => job.removedCandidates.indexOf(resume.userRef.id) === -1)
  }

  const numCandidates = candidates.length;
  const numScreenings = screenings.docs.length;
  const numResumes = resumes.docs.length;

  return {
    candidates: sortedCandidates,
    numCandidates,
    numScreenings,
    numResumes
  };
}

const getByReference = async (ref, fields) => {
  const data =  (await ref.get()).data();  
  let user = {};

  if(fields) {
    fields.forEach(field => {
      user[field] = data[field];
    });
  }else {
    user = data;
  }

  return user || { error: "the user could not be found", ref };
}

const getUserNameFromReference = async ref => {
  const user = await getByReference(ref);

  return `${user.fname} ${user.lname}`;
}

const getUserNameFromUID = async uid => {
  return (await model.doc(uid).get()).data()
}

const update = async (id, data, merge = true) => {
  try {
    await model.doc(id).set(data, { merge });
  } catch (error) {
    console.error(error);
  }
}

const getRef = async userId => {
  const ref = await db.doc(`userdata/${userId}`);
  return ref;
}

// validates each field from the post with the rules defined
// in the base template.
export default {
  model,
  currentUser,
  getAllUsers,
  deleteById,
  getRef,
  create,
  verify,
  getById,
  getByEmail,
  getCandidatesForJob,
  getByReference,
  getUserNameFromReference,
  getUserNameFromUID,
  update,
  getTemplate
};
